import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import AdminBilling from "@/components/AdminBilling";
import DataPrewarm from "@/components/DataPrewarm";
import { RefreshCw, Lock, FileText, ArrowLeft, Loader2, Receipt, AlertTriangle, PlayCircle, CheckCircle2 } from "lucide-react";

const STATUS = {
  draft: { l: "Draft", cls: "bg-amber-100 text-amber-800" },
  locked: { l: "Locked", cls: "bg-slate-900 text-white" },
  invoiced: { l: "Invoiced", cls: "bg-emerald-100 text-emerald-800" },
};

/** Month-end worklist: run every client's bill from one page, then drill into one. */
export default function MonthlyBillingRun({ accounts = [] }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - (d.getDate() <= 5 ? 1 : 0));
    return d.toISOString().slice(0, 7);
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(null);
  const [openAccount, setOpenAccount] = useState(null);
  const [job, setJob] = useState(null);
  const [y, m] = month.split("-");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/billing-run?year=${y}&month=${m}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  // Poll the background run while it works through the client list.
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data: p } = await api.get(`/admin/billing-run/progress?year=${y}&month=${m}`);
        if (stop) return;
        setJob(p.status === "idle" ? null : p);
        if (p.status === "running") setTimeout(tick, 3000);
        else if (p.status === "done") load();
      } catch (e) { /* noop */ }
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [month]);

  const calculateAll = async () => {
    try {
      const { data: p } = await api.post(`/admin/billing-run/calculate-all?year=${y}&month=${m}`, {});
      setJob({ ...p, status: "running" });
      toast.success(`Calculating ${p.total} client${p.total === 1 ? "" : "s"} in the background`);
      const poll = async () => {
        const { data: pr } = await api.get(`/admin/billing-run/progress?year=${y}&month=${m}`);
        setJob(pr.status === "idle" ? null : pr);
        if (pr.status === "running") setTimeout(poll, 3000);
        else { await load(); toast.success("All clients calculated"); }
      };
      setTimeout(poll, 2500);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const refreshOne = async (c) => {
    setRefreshing(c.account_id);
    try {
      await api.post(`/admin/billing-run/refresh?account_id=${c.account_id}&year=${y}&month=${m}`, {});
      toast.success(`${c.company_name} recalculated`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setRefreshing(null); }
  };

  if (openAccount) {
    const acc = accounts.find((a) => a.id === openAccount.account_id);
    return (
      <div className="space-y-4" data-testid="monthly-bill-workspace">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button data-testid="back-to-run" onClick={() => { setOpenAccount(null); load(); }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600">
              <ArrowLeft className="w-4 h-4" /> Back to {data?.month_label || "the month"}
            </button>
            <span className="text-slate-300">|</span>
            <p className="text-base font-bold text-slate-900">{openAccount.company_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <a data-testid="statement-pdf-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/accounts/${openAccount.account_id}/billing/statement.pdf?year=${y}&month=${m}`}
              className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-3.5 py-2 rounded-md text-sm">
              <FileText className="w-4 h-4" /> Customer statement (PDF)
            </a>
            <a data-testid="statement-xlsx-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/accounts/${openAccount.account_id}/billing/statement.xlsx?year=${y}&month=${m}`}
              className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-3.5 py-2 rounded-md text-sm">
              <FileText className="w-4 h-4" /> Statement (Excel)
            </a>
          </div>
        </div>
        <AdminBilling accounts={acc ? [acc] : accounts} fixedAccountId={openAccount.account_id} view="monthly" initialMonth={month} />
      </div>
    );
  }

  const clients = (data?.clients || []).filter((c) => c.billing_enabled !== false);

  return (
    <div className="space-y-4" data-testid="monthly-billing-run">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Run the month</h2>
            <p className="text-sm text-slate-500 mt-0.5">Recalculate each client, review credits and charges, then lock and invoice. Pricing lives in Rate Cards.</p>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="run-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
            <button data-testid="calculate-all-btn" onClick={calculateAll} disabled={job?.status === "running"}
              className="inline-flex items-center gap-2 bg-bh-blue hover:opacity-90 disabled:opacity-60 text-white font-semibold px-3.5 py-2 rounded-md text-sm transition-opacity">
              {job?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Calculate all
            </button>
            <button data-testid="run-reload" onClick={load} disabled={loading}
              className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-3.5 py-2 rounded-md text-sm">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reload
            </button>
          </div>
        </div>

        {job && (
          <div data-testid="calculate-all-progress" className={`mt-4 rounded-lg border px-4 py-3 ${job.status === "running" ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200"}`}>
            <div className="flex items-center justify-between gap-3 text-sm font-semibold text-slate-800">
              <span className="inline-flex items-center gap-2">
                {job.status === "running"
                  ? <><Loader2 className="w-4 h-4 animate-spin text-blue-600" /> Calculating {job.current ? <span className="text-blue-700">{job.current}</span> : "…"}</>
                  : <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Last run finished</>}
              </span>
              <span data-testid="calculate-all-count" className="text-slate-600">{job.done} of {job.total}</span>
            </div>
            <div className="mt-2 h-1.5 bg-white/70 rounded-full overflow-hidden">
              <div className="h-full bg-bh-blue transition-all duration-500" style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 0}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              One client at a time to stay inside ShipStation's rate limit.
              {job.skipped_locked ? ` ${job.skipped_locked} locked month${job.skipped_locked === 1 ? "" : "s"} skipped.` : ""}
            </p>
            {(job.errors || []).length > 0 && (
              <ul className="mt-2 text-xs text-red-700 space-y-0.5" data-testid="calculate-all-errors">
                {job.errors.map((e) => <li key={e.company_name || e.account_id}>{e.company_name}: {e.error}</li>)}
              </ul>
            )}
          </div>
        )}

        <DataPrewarm />


        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4" data-testid="run-totals">
            <Tile label="To be billed" value={formatCurrency(data.totals.to_be_billed)} accent />
            <Tile label="Credits given" value={formatCurrency(data.totals.credits_total)} />
            <Tile label="Draft" value={data.totals.draft} />
            <Tile label="Locked" value={data.totals.locked} />
            <Tile label="Invoiced" value={data.totals.invoiced} />
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="run-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2.5 px-4">Client</th>
              <th className="py-2.5 px-4">Status</th>
              <th className="py-2.5 px-4 text-right">Shipments</th>
              <th className="py-2.5 px-4 text-right">Credits</th>
              <th className="py-2.5 px-4 text-right">Charges</th>
              <th className="py-2.5 px-4 text-right">To be billed</th>
              <th className="py-2.5 px-4">Last calculated</th>
              <th className="py-2.5 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const t = c.totals || {};
              const st = STATUS[c.status] || STATUS.draft;
              return (
                <tr key={c.account_id} data-testid={`run-row-${c.account_id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="py-2.5 px-4">
                    <button onClick={() => setOpenAccount(c)} data-testid={`open-bill-${c.account_id}`} className="font-semibold text-slate-900 hover:text-blue-600 text-left">
                      {c.company_name}
                    </button>
                    <div className="text-xs text-slate-400">
                      {c.stores_linked === 0
                        ? <span className="text-amber-600 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> no store linked</span>
                        : `${c.stores_linked} store${c.stores_linked === 1 ? "" : "s"} linked`}
                    </div>
                  </td>
                  <td className="py-2.5 px-4">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${st.cls}`}>
                      {c.locked && <Lock className="w-3 h-3" />}{st.l}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-right">{t.order_count ?? "—"}</td>
                  <td className="py-2.5 px-4 text-right">{c.credits_total ? formatCurrency(c.credits_total) : "—"}</td>
                  <td className="py-2.5 px-4 text-right">{c.charges_count || "—"}</td>
                  <td className="py-2.5 px-4 text-right font-bold text-slate-900">{t.to_be_billed != null ? formatCurrency(t.to_be_billed) : <span className="text-slate-300">not calculated</span>}</td>
                  <td className="py-2.5 px-4 text-xs text-slate-500">{c.computed_at ? new Date(c.computed_at).toLocaleString() : "—"}</td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <button data-testid={`refresh-bill-${c.account_id}`} onClick={() => refreshOne(c)} disabled={refreshing === c.account_id || c.stores_linked === 0}
                      className="inline-flex items-center gap-1 text-slate-600 hover:text-blue-700 disabled:opacity-40 text-xs font-semibold">
                      {refreshing === c.account_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Calculate
                    </button>
                    <button data-testid={`review-bill-${c.account_id}`} onClick={() => setOpenAccount(c)}
                      className="ml-3 inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-semibold">
                      <Receipt className="w-3.5 h-3.5" /> Review
                    </button>
                  </td>
                </tr>
              );
            })}
            {clients.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400">{loading ? "Loading…" : "No clients with billing enabled."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        Figures come from the last calculation — ShipStation allows 40 API calls a minute, so clients are recalculated one at a time. A locked month always shows its frozen total.
      </p>
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className={`rounded-lg p-3 border ${accent ? "bg-orange-50 border-orange-200" : "bg-slate-50 border-slate-200"}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-lg font-bold ${accent ? "text-orange-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}
